import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/cn";
import { Image } from "../../primitives/Image";
import { BlockChildren, type BlockRef } from "./blockChildren";

/**
 * Carousel - a horizontal scroller of compact cards (id, title, subtitle,
 * image, optional actions). Renderer for the K2 `carousel` block
 * (`docs/contracts/block-registry.md`): the platform's chat `BlockRenderer`
 * passes a `carousel` block's `props` straight through, so the prop names match
 * the block payload exactly.
 *
 * Block props (`{ title?, description?, items }`):
 *   - `title?` / `description?` - optional header above the rail.
 *   - `items` - the cards. Each is EITHER a compact descriptor
 *     `{ id, name, subtitle?, image?, actions?: [{ id, label }] }` (the common
 *     case, lifted from tool-ui's Item Carousel), OR a nested `block_ref`
 *     (an inline block envelope) for richer cells. Block-ref cells render
 *     through the shared child-block resolver `grid` uses - the carousel is one
 *     of the composition blocks that share that one code path (catalog §5).
 *
 * A carousel is display-only until a card carries `actions`; then it is
 * interactive and a clicked action emits the shared block-submit event keyed by
 * `block_id` + the action id (see `blockSubmit.tsx`), reusing the form /
 * approval transport - no new mechanism.
 *
 * Client-only props (never part of the streamed block): `className`.
 *
 * Scrolls with native overflow + scroll-snap; prev / next buttons nudge by a
 * card width and are hidden when there is nothing to scroll to. Token-themed
 * (`bg-card`, `border-border`, ...) with no hardcoded colors. SSR-safe: the
 * scroll-position state initialises in an effect, never at module top.
 */

/** One action affordance on a carousel card. */
export interface CarouselAction {
  /** Stable id, sent back as the submit `action`. */
  id: string;
  /** Button label. */
  label: string;
}

/** Source: tool-ui Item Carousel (exact shape), OpenAI Apps SDK Carousel/List. */
export interface CarouselItem {
  /** Stable id for the card. */
  id: string;
  /** Card title. */
  name: string;
  /** Optional subtitle / secondary line. */
  subtitle?: string;
  /** Optional thumbnail image URL. */
  image?: string;
  /** Optional action affordances; their presence makes the carousel interactive. */
  actions?: CarouselAction[];
}

/** A carousel item is a compact card descriptor or a nested block reference. */
export type CarouselEntry = CarouselItem | BlockRef;

const carouselVariants = cva("not-prose flex flex-col gap-3");

/** True when an entry is a compact `CarouselItem` (has `name`) vs a `BlockRef`. */
function isCard(entry: CarouselEntry): entry is CarouselItem {
  return typeof entry === "object" && entry !== null && "name" in entry;
}

export interface CarouselProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof carouselVariants> {
  /** Optional header title. */
  title?: string;
  /** Optional header description. */
  description?: string;
  /** The cards (compact descriptors and/or nested block refs). Required. */
  items: CarouselEntry[];
  /** Action handler for card actions. Wired by the block adapter. */
  onAction?: (itemId: string, actionId: string) => void;
}

export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(function Carousel(
  { className, title, description, items, onAction, ...rest },
  ref,
) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, items.length]);

  const nudge = React.useCallback((dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  }, []);

  const cardEntries = items.filter(isCard);
  const refEntries = items.filter((it): it is BlockRef => !isCard(it));

  return (
    <div ref={ref} className={cn(carouselVariants(), className)} data-slot="carousel" {...rest}>
      {title || description || canScrollLeft || canScrollRight ? (
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            {title ? (
              <p className="text-sm font-semibold leading-snug text-foreground">{title}</p>
            ) : null}
            {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
          </div>
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={() => nudge(-1)}
              disabled={!canScrollLeft}
              aria-label="Scroll left"
              className="inline-flex size-7 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => nudge(1)}
              disabled={!canScrollRight}
              aria-label="Scroll right"
              className="inline-flex size-7 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={scrollerRef}
        className={cn(
          "flex gap-3 overflow-x-auto pb-1",
          "snap-x snap-mandatory scroll-smooth",
          "[scrollbar-width:thin]",
        )}
        role="group"
        aria-label={title ?? "Carousel"}
      >
        {cardEntries.map((item) => (
          <CarouselCard key={item.id} item={item} onAction={onAction} />
        ))}
        {refEntries.length > 0 ? (
          <BlockChildren
            items={refEntries}
            renderItem={(node, index) => (
              <div
                key={`ref-${index}`}
                className="w-64 shrink-0 snap-start"
                data-slot="carousel-block-cell"
              >
                {node}
              </div>
            )}
          />
        ) : null}
      </div>
    </div>
  );
});

Carousel.displayName = "Carousel";

/* ============================================================================
 * Internal - one compact card
 * ========================================================================= */

function CarouselCard({
  item,
  onAction,
}: {
  item: CarouselItem;
  onAction?: (itemId: string, actionId: string) => void;
}) {
  return (
    <div
      className={cn(
        // A nested mini-card inside the rail: keep the tighter `rounded-xl`
        // scale (the rail itself is borderless), but adopt the same hairline
        // border opacity as the top-level block surfaces.
        "flex w-56 shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-border/70 bg-card",
        "transition-shadow hover:shadow-[0_8px_30px_-12px_rgb(0_0_0_/_0.12)]",
      )}
      data-slot="carousel-card"
    >
      {item.image ? (
        <Image
          src={item.image}
          alt={item.name}
          aspectRatio="16 / 10"
          rounded="none"
          className="w-full"
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1 p-3">
        <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
        {item.subtitle ? (
          <p className="line-clamp-2 text-xs text-muted-foreground">{item.subtitle}</p>
        ) : null}
        {item.actions && item.actions.length > 0 ? (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
            {item.actions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => onAction?.(item.id, action.id)}
                className={cn(
                  "inline-flex items-center rounded-full border border-border px-2.5 py-1 text-xs font-medium text-foreground",
                  "transition-colors hover:bg-accent/10 hover:text-accent hover:border-accent",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { carouselVariants };
export type CarouselVariantProps = VariantProps<typeof carouselVariants>;
